import React from "react";
import { motion } from "framer-motion";
import { Star, Luggage, Clock, Waves, ArrowRight, CalendarRange } from "lucide-react";
import { eur } from "../lib/api";
import { useI18n, badgeKey, verdictKey } from "../i18n";
import { tripDateContext } from "../lib/goal";

const BADGE_STYLE = {
  "BEST MATCH": { bg: "var(--ink)", fg: "#fff" },
  "BEST VALUE": { bg: "var(--book)", fg: "#fff" },
  "BEST HOTEL": { bg: "var(--brand)", fg: "#fff" },
};

export const TripCard = ({ trip, rank, onSelect, goal }) => {
  const { t, lang } = useI18n();
  const dateContext = tripDateContext(goal, lang);
  const badge = BADGE_STYLE[trip.badge] || { bg: "var(--ink-soft)", fg: "#fff" };
  const bk = badgeKey(trip.badge);
  return (
    <motion.button
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: rank * 0.12, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => onSelect(trip)}
      data-testid={`trip-card-${trip.id}`}
      className="dv-card dv-hover text-left w-full overflow-hidden group"
    >
      <div className="relative h-44 sm:h-48 overflow-hidden">
        <img src={trip.image} alt={trip.destination} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,0) 40%, rgba(15,23,42,0.55) 100%)" }} />
        {bk && (
          <span className="absolute top-3 left-3 dv-pill text-[11px] px-3 py-1.5 font-display uppercase tracking-wide" style={{ background: badge.bg, color: badge.fg }}>
            {t(bk)}
          </span>
        )}
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between text-white">
          <div>
            <div className="font-display font-bold text-xl leading-tight">{trip.destination}</div>
            <div className="text-xs opacity-90">{trip.region} · {trip.country}</div>
          </div>
          <div className="dv-pill text-[11px] px-2 py-1 bg-white/90 text-slate-900">{trip.match_score}{t("results.matchSuffix")}</div>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.22em] text-slate-400 font-display">{t("common.total")}</div>
            <div className="dv-total text-3xl sm:text-[34px] leading-none text-slate-900">{eur(trip.total)}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-slate-400">{t("results.cBudget")}</div>
            <div className="font-display font-bold text-base" style={{ color: trip.budget_left >= 0 ? "var(--book-ink)" : "var(--alert)" }}>
              {trip.budget_left >= 0 ? eur(trip.budget_left) : `-${eur(-trip.budget_left)}`}
            </div>
          </div>
        </div>

        <div className="mt-2 text-[11px] text-slate-500 font-mono-num">
          {t("landing.flights")} + {t("results.cHotel")} + {t("plan.prefBags")} + {t("results.cTransfers")}
        </div>
        {dateContext && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700" data-testid={`trip-date-${trip.id}`}>
            <CalendarRange size={13} color="var(--brand)" /> {dateContext.label}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-1.5">
          <Chip icon={Star} text={t("results.hotelChip", { r: trip.hotel_rating })} />
          <Chip icon={Clock} text={`${trip.travel_hours}h`} />
          <Chip icon={Luggage} text={trip.baggage_included ? t("results.bagsIncl") : t("results.noBags")} on={trip.baggage_included} />
          <Chip icon={Waves} text={t("results.pool")} />
        </div>

        <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{trip.summary}</p>

        <div className="mt-4 flex items-center justify-between">
          <span className="dv-pill text-xs px-2.5 py-1" style={{ background: trip.decision === "BOOK" ? "var(--book-soft)" : "var(--wait-soft)", color: trip.decision === "BOOK" ? "var(--book-ink)" : "var(--wait-ink)" }}>
            {trip.decision === "BOOK" ? t("results.recBook") : t("results.recWatch")}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold font-display text-slate-900 group-hover:gap-2 transition-all">
            {t("results.seeDecision")} <ArrowRight size={16} />
          </span>
        </div>
      </div>
    </motion.button>
  );
};

const Chip = ({ icon: Icon, text, on = true }) => (
  <span className="dv-pill text-[11px] px-2.5 py-1" style={{ background: "var(--paper)", color: on ? "var(--ink-soft)" : "var(--muted)", border: "1px solid var(--line)" }}>
    <Icon size={12} /> {text}
  </span>
);
